import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Ambulance as AmbIcon, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useGeo } from "@/context/LocationContext";

const STATUS_COPY = {
  REQUESTED: "Request sent — no provider has accepted yet", ACCEPTED: "Accepted by provider — not yet on the way",
  DISPATCHED: "Provider reports the ambulance is on the way", ARRIVED: "Provider reports arrival at pickup",
  COMPLETED: "Trip completed", DECLINED_ALL: "All providers declined — call 108 / 112", CANCELLED: "Cancelled",
};
const err = (e) => toast.error(e?.response?.data?.detail || e.message);

function PublicNumbers() {
  return (
    <div className="grid grid-cols-2 gap-2">
      <a href="tel:108" data-testid="amb-call-108" className="h-14 rounded-2xl bg-red-600 text-white font-black flex items-center justify-center gap-2"><Phone size={18}/> 108 Ambulance</a>
      <a href="tel:112" data-testid="amb-call-112" className="h-14 rounded-2xl bg-stone-900 text-white font-black flex items-center justify-center gap-2"><Phone size={18}/> 112 Emergency</a>
    </div>
  );
}

function TripLinks({ r, asPatient }) {
  const { t } = useI18n();
  const pl = r.provider_location;
  return (
    <div className="flex flex-wrap gap-3 text-xs font-bold pt-1">
      {r.conv_id && <Link to={`/messenger/${r.conv_id}`} className="underline text-sky-700" data-testid={`amb-chat-${r.request_id}`}>{t("amb.open_chat", "Open trip chat")}</Link>}
      {asPatient && r.provider_contact?.phone && <a href={`tel:${r.provider_contact.phone}`} className="underline" data-testid={`amb-call-provider-${r.request_id}`}>{t("amb.call_provider", "Call {org}", { org: r.provider_contact.org_name })}</a>}
      {asPatient && pl && <a href={`https://www.openstreetmap.org/?mlat=${pl.lat}&mlon=${pl.lng}#map=16/${pl.lat}/${pl.lng}`} target="_blank" rel="noopener noreferrer"
                className="underline text-emerald-700" data-testid={`amb-provider-loc-${r.request_id}`}>{t("amb.provider_location", "Ambulance location (shared {time})", { time: new Date(pl.at).toLocaleTimeString() })}</a>}
    </div>
  );
}

function RequestCard({ r, asPatient = false }) {
  const { t } = useI18n();
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-3 text-sm space-y-1" data-testid={`amb-req-${r.request_id}`}>
      <div className="font-black" data-testid="amb-req-status">{t(`amb.status.${r.status}`, STATUS_COPY[r.status] || r.status)}</div>
      <div className="text-stone-600">{r.vehicle_type} · {r.pickup_text}</div>
      {r.destination_hospital && <div className="text-stone-600" data-testid="amb-req-destination">{t("amb.to", "To")}: {r.destination_hospital}</div>}
      <TripLinks r={r} asPatient={asPatient} />
      <ol className="text-xs text-stone-500 ps-3 border-s-2 border-stone-200">{r.timeline.map((ev, i) => <li key={i}>{new Date(ev.at).toLocaleTimeString()} · {ev.note}</li>)}</ol>
    </div>
  );
}

function PatientTab() {
  const { t } = useI18n();
  const { loc } = useGeo();
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState([]);
  const [form, setForm] = useState({ pickup_text: "", destination_hospital: "", share_location: false, share_phone: false, vehicle_type: "BLS" });
  const pickedTypes = [...new Set((items || []).filter((p) => sel.includes(p.provider_id)).map((p) => p.vehicle_type))];
  const [mine, setMine] = useState([]);
  const hasLoc = typeof loc?.lat === "number" && loc.source !== "default";
  const liveReq = mine.find((r) => ["REQUESTED", "ACCEPTED", "DISPATCHED", "ARRIVED"].includes(r.status));
  const loadMine = useCallback(() => api.get("/ambulance/requests/mine").then((r) => setMine(r.data.items)).catch(() => {}), []);
  useEffect(() => { loadMine(); }, [loadMine]);
  useEffect(() => { if (hasLoc) api.get("/ambulance/nearby", { params: { lat: loc.lat, lng: loc.lng } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [hasLoc, loc]);
  const send = async () => {
    if (!window.confirm("Send this request to the selected providers? They will see it, but no one is dispatched until they accept.")) return;
    try { await api.post("/ambulance/requests", { ...form, destination_hospital: form.destination_hospital.trim() || null, vehicle_type: pickedTypes.length === 1 ? pickedTypes[0] : form.vehicle_type, provider_ids: sel, lat: loc.lat, lng: loc.lng }); toast.success("Request sent"); setSel([]); loadMine(); }
    catch (e) { err(e); }
  };
  return (
    <div className="space-y-3" data-testid="amb-patient">
      {liveReq ? <p className="text-sm text-stone-600" data-testid="amb-live-request-note">{t("amb.live_note", "You already have an active request below. Cancel it or wait for it to finish before sending another.")}</p>
        : !hasLoc ? <p className="text-sm text-stone-600" data-testid="amb-need-location">Set your location (top bar) to see verified private ambulances nearby. Public 108/112 always work.</p>
        : items === null ? <p className="text-sm">Checking verified providers…</p>
        : items.length === 0 ? <p className="text-sm text-stone-600" data-testid="amb-none">No verified private ambulance is online near you right now. Call 108.</p>
        : (
          <ul className="space-y-2" data-testid="amb-provider-list">
            {items.map((p) => (
              <li key={p.provider_id}>
                <label className="flex items-center gap-2 rounded-xl border border-stone-200 bg-white p-3 text-sm">
                  <input type="checkbox" checked={sel.includes(p.provider_id)} data-testid={`amb-pick-${p.provider_id}`}
                         onChange={(e) => setSel(e.target.checked ? [...sel, p.provider_id] : sel.filter((x) => x !== p.provider_id))} />
                  <span className="font-bold">{p.org_name}</span><span className="text-stone-500">{p.vehicle_type} · {p.distance_km} km · verified · seen {new Date(p.last_seen).toLocaleTimeString()}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      {!liveReq && sel.length > 0 && (
        <div className="rounded-2xl border border-stone-200 bg-white p-3 space-y-2">
          <select className="w-full h-11 rounded-xl border border-stone-200 px-3 text-sm" value={pickedTypes.length === 1 ? pickedTypes[0] : form.vehicle_type}
                  onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} data-testid="amb-vehicle-select">
            <option value="BLS">Basic life support</option><option value="ALS">Advanced life support</option><option value="patient_transport">Patient transport</option>
          </select>
          <input className="w-full h-11 rounded-xl border border-stone-200 px-3 text-sm" placeholder="Pickup landmark / address" value={form.pickup_text}
                 onChange={(e) => setForm({ ...form, pickup_text: e.target.value })} data-testid="amb-pickup-input" />
          <input className="w-full h-11 rounded-xl border border-stone-200 px-3 text-sm" placeholder={t("amb.destination_ph", "Destination hospital (optional)")} value={form.destination_hospital}
                 onChange={(e) => setForm({ ...form, destination_hospital: e.target.value })} data-testid="amb-destination-input" />
          <label className="flex gap-2 text-sm"><input type="checkbox" checked={form.share_location} onChange={(e) => setForm({ ...form, share_location: e.target.checked })} data-testid="amb-share-location"/> Share my exact location with the provider who accepts</label>
          <label className="flex gap-2 text-sm"><input type="checkbox" checked={form.share_phone} onChange={(e) => setForm({ ...form, share_phone: e.target.checked })} data-testid="amb-share-phone"/> Share my phone number with the provider who accepts</label>
          <button onClick={send} disabled={form.pickup_text.trim().length < 3} data-testid="amb-send" className="w-full h-11 rounded-full bg-red-600 text-white font-black disabled:opacity-50">Request selected ({sel.length})</button>
        </div>
      )}
      {mine.map((r) => <RequestCard key={r.request_id} r={r} asPatient />)}
    </div>
  );
}

function ProviderTab() {
  const { t } = useI18n();
  const [me, setMe] = useState(undefined);
  const [inbox, setInbox] = useState([]);
  const [f, setF] = useState({ org_name: "", vehicle_type: "BLS", reg_no: "", phone: "", radius_km: 15 });
  const { loc } = useGeo();
  const load = useCallback(() => {
    api.get("/ambulance/provider/me").then((r) => { setMe(r.data); return api.get("/ambulance/provider/inbox"); })
      .then((r) => r && setInbox(r.data.items)).catch(() => setMe(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  const [busy, setBusy] = useState(false);
  const act = (fn, ok) => { if (busy) return; setBusy(true); fn().then(() => { if (ok) toast.success(ok); return load(); }).catch(err).finally(() => setBusy(false)); };
  if (me === undefined) return <p className="text-sm">Loading…</p>;
  if (me === null) {
    return (
      <div className="rounded-2xl border border-stone-200 bg-white p-3 space-y-2" data-testid="amb-register">
        {["org_name", "reg_no", "phone"].map((k) => (
          <input key={k} className="w-full h-11 rounded-xl border border-stone-200 px-3 text-sm" placeholder={{ org_name: "Organisation", reg_no: "Vehicle registration", phone: "Dispatch phone" }[k]}
                 value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`amb-reg-${k}`} />
        ))}
        <select className="w-full h-11 rounded-xl border border-stone-200 px-3 text-sm" value={f.vehicle_type} onChange={(e) => setF({ ...f, vehicle_type: e.target.value })} data-testid="amb-reg-vehicle">
          <option value="BLS">Basic life support</option><option value="ALS">Advanced life support</option><option value="patient_transport">Patient transport</option>
        </select>
        <button disabled={busy || typeof loc?.lat !== "number" || loc.source === "default"} data-testid="amb-reg-submit" className="w-full h-11 rounded-full bg-stone-900 text-white font-black disabled:opacity-50"
                onClick={() => act(() => api.post("/ambulance/provider", { ...f, base_lat: loc.lat, base_lng: loc.lng }))}>Register (base = current location)</button>
        <p className="text-xs text-stone-500">An admin must verify you before you can go online.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="amb-provider">
      <div className="rounded-2xl border border-stone-200 bg-white p-3 flex items-center gap-2 text-sm">
        <b>{me.org_name}</b><span className="text-stone-500">{me.verified ? "Verified" : "Awaiting verification"}</span>
        <button disabled={!me.verified || busy} data-testid="amb-online-toggle" className={`ms-auto h-9 px-4 whitespace-nowrap text-xs rounded-full font-bold ${me.online ? "bg-emerald-600 text-white" : "border border-stone-300"} disabled:opacity-50`}
                onClick={() => act(() => api.post("/ambulance/provider/online", { online: !me.online, lat: loc?.lat, lng: loc?.lng }), me.online ? "You are offline" : "You are online")}>{busy ? "…" : me.online ? "Online · go offline" : "Go online"}</button>
      </div>
      {me.online && <p className="text-xs text-stone-500">Stay online by refreshing at least every 30 minutes — otherwise patients stop seeing you.</p>}
      {inbox.map((r) => (
        <div key={r.request_id} className="space-y-2">
          <RequestCard r={r} />
          {r.lat && <a className="text-sm underline text-sky-700 me-3" href={`https://www.openstreetmap.org/directions?to=${r.lat},${r.lng}`} data-testid="amb-nav-link">Navigate to pickup</a>}
          {r.patient_phone && <a className="ms-3 me-3 text-sm underline" href={`tel:${r.patient_phone}`} data-testid="amb-call-patient">Call patient</a>}
          {r.status === "REQUESTED" && !r.my_response && (
            <div className="flex gap-2">
              <button disabled={busy} data-testid={`amb-accept-${r.request_id}`} className="disabled:opacity-50 h-9 px-4 rounded-full bg-emerald-600 text-white font-bold" onClick={() => act(() => api.post(`/ambulance/requests/${r.request_id}/respond`, { action: "accept" }), "Accepted — patient notified")}>Accept</button>
              <button disabled={busy} data-testid={`amb-decline-${r.request_id}`} className="disabled:opacity-50 h-9 px-4 rounded-full border border-stone-300 font-bold" onClick={() => act(() => api.post(`/ambulance/requests/${r.request_id}/respond`, { action: "decline" }))}>Decline</button>
            </div>
          )}
          {r.assigned_provider_id === me.provider_id && ["ACCEPTED", "DISPATCHED", "ARRIVED"].includes(r.status) && (
            <button disabled={busy || typeof loc?.lat !== "number" || loc.source === "default"} data-testid={`amb-share-loc-${r.request_id}`}
                    className="disabled:opacity-50 h-9 px-4 me-2 rounded-full border border-emerald-600 text-emerald-700 font-bold"
                    onClick={() => act(() => api.post(`/ambulance/requests/${r.request_id}/location`, { lat: loc.lat, lng: loc.lng }), t("amb.loc_shared", "Location shared with patient"))}>{t("amb.share_location", "Share my location")}</button>
          )}
          {r.assigned_provider_id === me.provider_id && { ACCEPTED: "DISPATCHED", DISPATCHED: "ARRIVED", ARRIVED: "COMPLETED" }[r.status] && (
            <button disabled={busy} data-testid={`amb-next-${r.request_id}`} className="disabled:opacity-50 h-9 px-4 rounded-full bg-stone-900 text-white font-bold"
                    onClick={() => act(() => api.post(`/ambulance/requests/${r.request_id}/status`, { status: { ACCEPTED: "DISPATCHED", DISPATCHED: "ARRIVED", ARRIVED: "COMPLETED" }[r.status] }))}>
              Mark {{ ACCEPTED: "on the way", DISPATCHED: "arrived", ARRIVED: "completed" }[r.status]}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AmbulancePage() {
  const { user } = useAuth();
  const [p, setP] = useSearchParams();
  const tab = p.get("tab") === "provider" ? "provider" : "patient";
  return (
    <div className="max-w-xl mx-auto px-4 py-6 space-y-4" data-testid="ambulance-page">
      <h1 className="text-2xl font-black flex items-center gap-2"><AmbIcon className="text-red-600" /> Ambulance</h1>
      <PublicNumbers />
      {!user ? <p className="text-sm" data-testid="amb-login-hint"><Link to="/login" className="underline font-bold">Sign in</Link> to request a verified private ambulance.</p> : (
        <>
          <div className="flex gap-2">
            {["patient", "provider"].map((k) => (
              <button key={k} onClick={() => setP({ tab: k })} data-testid={`amb-tab-${k}`}
                      className={`h-9 px-4 rounded-full text-sm font-bold ${tab === k ? "bg-stone-900 text-white" : "border border-stone-300"}`}>{k === "patient" ? "I need an ambulance" : "I'm a provider"}</button>
            ))}
          </div>
          {tab === "patient" ? <PatientTab /> : <ProviderTab />}
        </>
      )}
      <p className="text-xs text-stone-500">Private providers are listed only when verified and online. Find2Near does not estimate arrival times or dispatch public ambulances.</p>
    </div>
  );
}
